import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { login } from "../../Firebase/authService";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { GoogleLogin } from "@react-oauth/google";
import "./Login.css";

const API_URL =
  process.env.REACT_APP_API_URL ||
  "https://automotive-backend-production.up.railway.app";

function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const navigate = useNavigate();

  const checkIfAdmin = async (email) => {
    const db = getFirestore();
    const docRef = doc(db, "admins", email);
    const docSnap = await getDoc(docRef);

    return docSnap.exists() && docSnap.data().role === "admin";
  };

  const formatFirebaseError = (message) => {
    if (message.includes("auth/invalid-email")) {
      return "Please enter a valid email address.";
    }

    if (message.includes("auth/user-not-found")) {
      return "No account found with this email.";
    }

    if (message.includes("auth/wrong-password")) {
      return "Incorrect password. Please try again.";
    }

    if (message.includes("auth/invalid-credential")) {
      return "Invalid email or password.";
    }

    if (message.includes("auth/too-many-requests")) {
      return "Too many attempts. Please try again later.";
    }

    return "Failed to login. Please check your details and try again.";
  };

  const handleEmailLogin = async (e) => {
    e.preventDefault();
    setErrorMsg("");

    if (!email.trim() || !password.trim()) {
      setErrorMsg("Please enter your email and password.");
      return;
    }

    try {
      setLoading(true);

      const userCredential = await login(email, password);
      const user = userCredential.user;

      const isAdmin = await checkIfAdmin(user.email);

      if (isAdmin) {
        navigate("/admin");
      } else {
        navigate("/");
      }
    } catch (err) {
      console.error("Email login error:", err);
      setErrorMsg(formatFirebaseError(err.message || ""));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async (credentialResponse) => {
    setErrorMsg("");

    try {
      setGoogleLoading(true);

      const credential = credentialResponse?.credential;

      if (!credential) {
        throw new Error("Google credential not received.");
      }

      const response = await fetch(`${API_URL}/users/google-login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ credential }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || "Google login failed on backend.");
      }

      const loggedInUser = result.user;

      localStorage.setItem("currentUser", JSON.stringify(loggedInUser));
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("loginProvider", "google");

      window.dispatchEvent(new Event("auth-change"));
      window.location.replace("/");
    } catch (err) {
      console.error("Google login error:", err);
      setErrorMsg(err.message || "Google sign-in failed.");
    } finally {
      setGoogleLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo-circle">
            <i className="bi bi-shield-lock-fill"></i>
          </div>

          <h2>Welcome Back</h2>
          <p>Login to access your Shipment Solutions account.</p>
        </div>

        {errorMsg && (
          <div className="login-alert error-alert">
            <i className="bi bi-exclamation-circle-fill"></i>
            <span>{errorMsg}</span>
          </div>
        )}

        <form onSubmit={handleEmailLogin} className="login-form">
          <div className="form-group">
            <label htmlFor="email">Email Address</label>

            <div className="input-wrapper">
              <i className="bi bi-envelope"></i>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <div className="password-label-row">
              <label htmlFor="password">Password</label>
            </div>

            <div className="input-wrapper">
              <i className="bi bi-lock"></i>

              <input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
              />

              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
              </button>
            </div>
          </div>

          <button type="submit" className="login-submit-btn" disabled={loading}>
            {loading ? (
              <>
                <span className="login-spinner"></span>
                Logging in...
              </>
            ) : (
              <>
                Login
                <i className="bi bi-arrow-right"></i>
              </>
            )}
          </button>
        </form>

        <div className="login-divider">
          <span></span>
          <p>or continue with</p>
          <span></span>
        </div>

        <div className="google-login-box">
          {googleLoading && (
            <div className="google-loading-text">
              <span className="login-spinner dark"></span>
              Connecting with Google...
            </div>
          )}

          <GoogleLogin
            onSuccess={handleGoogleLogin}
            onError={() => {
              setErrorMsg("Google Login Failed. Please try again.");
            }}
            theme="outline"
            size="large"
            shape="pill"
            text="signin_with"
            width="330"
          />
        </div>

        <div className="login-footer">
          <p>
            Don&apos;t have an account? <Link to="/signup">Create Account</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default Login;